import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function FoodPhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <Editorial />
      <Categories />
      <PlannerBlock
        eyebrow="Plan it now · Interactive estimator"
        headline="A signature shoot,"
        italic="costed in under a minute."
        blurb="Slide for menu size, tick what you want shot — drinks, desserts, reels — and see a clean estimate. We confirm the final price by phone the same business day."
      />
      <MelbourneSection
        serviceLabel="Food Photography"
        eyebrow="Melbourne hospitality · Mobile coverage"
        headline="A food photographer for"
        italic="Melbourne kitchens."
        intro="A mobile food photographer working with chefs, owners and marketing teams across Greater Melbourne — kitchens in Footscray, fine-dining in Richmond and CBD, café strips in Brunswick and Carlton, family restaurants out in Point Cook and Glen Waverley."
      />
      <SisterServices exclude="food" />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on"
        italic="food photography."
        theme="ink"
        cols={3}
        items={blogsForService("food-photography")}
      />
      <FAQSection
        eyebrow="Common questions · Food photography Melbourne"
        headline="Plating, props,"
        italic="and turnaround."
        intro="Most quotes are confirmed in under five minutes by phone. If you've got a launch date locked, mention it on the call — we routinely turn around hero plates in 72 hours."
        faqs={FOOD_FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to shoot"
        italic="your hero plates?"
        intro="Quick form below or call directly — quotes confirmed inside one business day. No marketing chase, no spam, no AI follow-ups."
        backdropSlug="menu-malai-tikka"
        formTitle="Tell us about your food shoot."
      />
      <Footer />
    </div>
  );
}

// ─── HERO ─────────────────────────────────────────────
const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo
        slug="menu-malai-tikka"
        alt="Food photography Melbourne — Malai Tikka hero plate"
        priority
        ratio="auto"
        className="!relative h-full w-full"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Food Photography Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Food Photography Melbourne · Restaurants · Chefs · Hospitality groups
        </p>

        <h1
          className="font-display tracking-editorial leading-[0.92]"
          style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}
        >
          Food photography
          <br />
          that looks <span className="italic font-light text-ember">like dinner.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Editorial-quality food &amp; beverage photography for Melbourne
          restaurants, chefs and hospitality groups. Hero plates, signature
          dishes, drinks and seasonal launches — shot in your kitchen, on your
          tables, with your team.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Half-day Hero Shoot · From $650
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          Half-day on-location, 6–10 hero frames, edited in 5 business days.
          Larger campaigns and full-day production quoted on the call.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Shoot Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan My Food Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Format", "Half / full-day"],
          ["Turnaround", "3–5 days"],
          ["Coverage", "All Melbourne"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── WHY ──────────────────────────────────────────────
const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why food photography Melbourne chefs invest in
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            One hero plate.
            <br />
            <span className="italic font-light text-ember">A whole season of marketing.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                A single, well-shot signature dish carries a launch poster, a
                Meta ad set, an EDM banner, the home page hero and three weeks
                of Instagram. Editorial food photography isn&apos;t decoration
                — it&apos;s <strong className="text-bone">the working asset behind every campaign you run.</strong>
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We shoot the way good food editors used to — natural light,
                tight crops, hands in frame, steam if there&apos;s steam, no
                plastic gloss. The dish is the photograph. Plating stays
                yours; we light it properly and frame it with intent.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Half a day on-location, six to ten hero frames, files sized for
                every channel — paid media, website, print, in-venue prints
                and your PR kit.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Editorial-grade hero plates",
                  "Drinks, cocktails & coffee",
                  "Signature dish launches",
                  "Seasonal menu campaigns",
                  "Print & outdoor-ready files",
                  "Coordinated with your designer",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="menu-butter-chicken" alt="Hero plate food photography Melbourne" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="menu-falooda" alt="Beverage photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="hero-laddu-220" alt="Editorial food styling Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── EDITORIAL — single feature spread ────────────────
const Editorial = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Editorial · The hero frame
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            The first image carries
            <br />
            <span className="italic font-light text-ember">the whole campaign.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A campaign hero is a single photograph that has to work as a
            billboard, a website hero, an EDM, a print poster and seven social
            crops. We shoot for that brief from frame one — same dish,
            multiple usable crops, consistent palette.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-5">
        <div className="col-span-12 md:col-span-8">
          <Photo slug="hero-banquet-01" alt="Editorial food photography Melbourne — restaurant banquet" ratio="16/10" sizes="(min-width: 768px) 60vw, 100vw" />
        </div>
        <div className="col-span-12 md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-5">
          <Photo slug="hero-gulab-jamun-00" alt="Dessert photography Melbourne" ratio="1/1" sizes="20vw" />
          <Photo slug="menu-falooda" alt="Drinks photography Melbourne" ratio="1/1" sizes="20vw" />
        </div>
      </div>
    </div>
  </section>
);

// ─── CATEGORIES ───────────────────────────────────────
const CATEGORIES = [
  { n: "01", title: "Hero plates", slug: "menu-malai-tikka", desc: "Signature dishes shot to anchor a campaign — billboard, hero, print, EDM. One frame, every crop you need." },
  { n: "02", title: "Drinks & cocktails", slug: "menu-falooda", desc: "Cocktails, mocktails, lassis, coffee — condensation, garnish, glassware. Bar shoots in working light." },
  { n: "03", title: "Macro & detail", slug: "hero-gulab-jamun-00", desc: "Pour shots, hidden layers, syrup catch, steam. Macro food photography that earns the scroll-stop." },
  { n: "04", title: "Editorial styling", slug: "hero-banquet-01", desc: "Multi-plate spreads, hands-in-frame, working tables. The shot that looks like a magazine, not a stock photo." },
  { n: "05", title: "Seasonal launches", slug: "menu-butter-chicken", desc: "New menus, seasonal specials, limited drops — a coordinated set of hero plates and supporting crops." },
  { n: "06", title: "Coffee & brunch", slug: "hero-spread-01", desc: "Latté art, brunch boards, café food in daylight. Bright, honest, share-ready imagery for cafés." },
  { n: "07", title: "Print & outdoor", slug: "menu-lamb-dosa", desc: "High-res files retouched for print, in-venue posters, transit and outdoor at any size — including CMYK on request." },
  { n: "08", title: "Brand collateral", slug: "hero-raj-kachori-08", desc: "Hero imagery sized for paid social, website, EDM, packaging and PR kits — delivered in one shareable folder." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Food & beverage
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Hero plates, drinks
            <br />
            <span className="italic font-light text-ember">and everything around them.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            One photographer, one library, every channel — paid, organic, web,
            print and PR. No studio shipping, no plastic styling, no AI
            placeholders.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Food photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── FAQS ─────────────────────────────────────────────
const FOOD_FAQS = [
  { q: "How is food photography different from menu photography?", a: "Menu photography prioritises clean, consistent, app-ready images of every dish. Food photography is editorial — fewer dishes, more time per shot, more styling and storytelling. We typically run them as separate shoots, but it's common to bolt a hero half-day onto a menu day." },
  { q: "Do you style and prop the food, or do we?", a: "Your kitchen plates. We bring lighting, backgrounds, surface boards and supplementary props (linens, glassware, cutlery). For larger campaigns we coordinate with a stylist if needed — quoted separately." },
  { q: "How many hero frames in a half-day shoot?", a: "Six to ten finished frames is typical for a half-day. A full-day on-location with proper styling typically delivers 12–18 editorial frames plus a folder of supporting crops." },
  { q: "Can I use the photos for print, outdoor and paid ads?", a: "Yes. Files are delivered web-ready and at full resolution (60+ MP). Print, transit, billboards, paid media, social, EDM and PR — all included in the licence at no extra cost." },
  { q: "How fast is turnaround for a launch campaign?", a: "Standard delivery is 3–5 business days. Urgent 48–72 hour turnarounds are available — confirm the deadline on the call and we'll lock it in writing." },
  { q: "Which Melbourne suburbs do you cover?", a: "All of Greater Melbourne — CBD, Carlton, Fitzroy, Brunswick, Richmond, South Yarra, St Kilda, Prahran, Footscray, Point Cook, Tarneit, Werribee, Sunshine, Epping, Craigieburn, Preston, Dandenong, Glen Waverley, Clayton and Springvale." },
];
